import { PROBLEM, isProblem } from '@/lib/api/problem';

/**
 * BP-01 §6 gives the staff `/login` screen the states "idle, submitting, error
 * (invalid, locked, readonly banner)". This maps an api failure onto exactly
 * those, by `urn:26ad:*` problem type rather than by status code or message.
 *
 * * `invalid` — `urn:26ad:unauthenticated`. The api answers this for a wrong
 *   email, a wrong password, a disabled user **and** a missing or wrong one-time
 *   code, with one indistinguishable detail string, deliberately
 *   (`api/app/modules/auth/service.py::_invalid_credentials`). The screen
 *   therefore offers the TOTP field after the first failure rather than claiming
 *   to know which half was wrong.
 * * `locked` — `urn:26ad:rate-limited`. BP-01 §9 rate-limits `/auth/*` to
 *   10/min per IP; from the user's side the account is locked for a minute.
 * * `readonly` — `urn:26ad:tenant-readonly` (R16).
 * * `unknown` — anything else, including a network failure, which arrives as a
 *   `TypeError` from `fetch` and is not a problem document at all.
 */
export type LoginErrorKind = 'invalid' | 'locked' | 'readonly' | 'unknown';

/** Classify a thrown login failure into one of §6's error states. */
export function classifyLoginError(error: unknown): LoginErrorKind {
  if (!isProblem(error)) {
    return 'unknown';
  }
  if (error.is(PROBLEM.unauthenticated)) {
    return 'invalid';
  }
  if (error.is(PROBLEM.rateLimited)) {
    return 'locked';
  }
  if (error.is(PROBLEM.tenantReadonly)) {
    return 'readonly';
  }
  return 'unknown';
}
